import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Button, CircularProgress, Grid, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { find, flatMap, includes, indexOf, isEmpty, map, reduce, size } from 'lodash';
import { useSnackbar } from 'notistack';
import React, { ReactElement, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { getChannelMarkers, getChannelMarkersQueryKey } from 'api/markerTypes';
import { getModel, runMultiplexBinaryClassifier } from 'api/platform';
import { getNormalizationSummary } from 'api/slideMultiplexChannelNormalizations';
import { getSlidesDataForArtifactsResults } from 'api/study';
import { useTrackActionFormDirty } from 'components/ActionDialog/actionFormDirtyState';
import { modelTypeBinaryClassifier } from 'components/Pages/Jobs/inferenceFieldsOptions';
import {
  NormalizationCoverageSummaryPanel,
  useNormalizationCoverageSummary,
} from 'components/StudyDashboard/QCMarkers/NormalizationCoverageSummary';
import { SlidePreviewCell } from 'components/atoms/DataGridRenderers/SlideIdCellRenderer';
import { getModelsTypeByModelInferences, ModelInference, SlideInferenceResults } from 'interfaces/calculateFeatures';
import { CellSegmentationConfig } from 'interfaces/inferenceArtifacts';
import { MapArtifactToSlides } from 'interfaces/jobs/basic';
import { Slide } from 'interfaces/slide';
import { MULTIPLEX_STAIN_ID } from 'interfaces/stainType';
import { getModelId } from 'utils/models';
import { useOrchestrationSelections } from 'utils/orchestrationSelections';
import { useCasesParams } from 'utils/useCasesParams';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import { ActionStepper } from '../ActionStepper';
import { ChannelsController } from '../ChannelsController';
import InferenceModelsForSlides from '../PostProcessingAndCalculateFeatures/InferenceModelsForSlides';
import { useArtifactResultsQuery } from '../PostProcessingAndCalculateFeatures/useArtifactsResultsQuery';
import { SelectModelStep } from '../SelectModelStep';
import { ExecutionRequirementsPanel } from '../requirements/ExecutionRequirementsPanel';
import { ExecutionRequirementType, useExecutionRequirements } from '../requirements/useExecutionRequirements';
import { getMapArtifactToSlides } from '../utils';
import MultiplexProcessSlideIds from './MultiplexProcessSlideIds';
import { MultiplexSelectionAlert } from './MultiplexSelectionAlert';
import { MarkerNormalizationGapAlert } from './RunMultiplexBinaryClassifier/MarkerNormalizationGapAlert';
import {
  getMarkerNormalizationAvailability,
  getMissingSelectedArtifactResultSlideIds,
} from './RunMultiplexBinaryClassifier/helpers';
import { buildCohortWideChannelSchema, getChannelMarkersInAllSlides } from './channelSelection';
import { useMultiplexActionSelection } from './useMultiplexActionSelection';

const SNACK_BAR_KEY_RUN_MULTIPLEX_BINARY_CLASSIFIER = 'RUN_MULTIPLEX_BINARY_CLASSIFIER';

export interface IFormValues {
  modelPath: string;
  channelNames: string[];
}

export enum RunMultiplexBinaryClassifierSteps {
  CheckProcesses = 'Check Required Upstream Results',
  SelectInputs = 'Select Inputs',
  SelectModel = 'Select Model',
}

const stepsOrder = [
  RunMultiplexBinaryClassifierSteps.SelectModel,
  RunMultiplexBinaryClassifierSteps.SelectInputs,
  RunMultiplexBinaryClassifierSteps.CheckProcesses,
];

const modelUrlValidation = yup.object({
  modelPath: yup.string().required(),
});

const formValidation = modelUrlValidation.shape({
  channelNames: yup.array().of(yup.string().required()).min(1, 'At least one marker is required').required(),
});

const defaultValues: IFormValues = { modelPath: '', channelNames: [] };

export interface IFormProps {
  onClose: (event?: any, force?: boolean) => void;
}

export const RunMultiplexBinaryClassifier = (props: IFormProps): ReactElement => {
  const formMethods = useForm<IFormValues>({
    mode: 'onChange',
    resolver: yupResolver(formValidation),
    defaultValues,
  });
  const { onClose } = props;
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const modelSelectionStepIndex = indexOf(stepsOrder, RunMultiplexBinaryClassifierSteps.SelectModel);
  const inputSelectionStepIndex = indexOf(stepsOrder, RunMultiplexBinaryClassifierSteps.SelectInputs);
  const checkProcessesStepIndex = indexOf(stepsOrder, RunMultiplexBinaryClassifierSteps.CheckProcesses);
  const [activeStepIndex, setActiveStepIndex] = useState(modelSelectionStepIndex);
  const [didAdvanceFromDefaultModel, setDidAdvanceFromDefaultModel] = useState(false);
  const [baselineModelPath, setBaselineModelPath] = useState(defaultValues.modelPath);
  const { casesParams } = useCasesParams();
  const { stainTypeIdToDisplayName } = useStainTypeIdToDisplayName();
  const {
    effectiveCasesParams,
    infoMessage: multiplexSelectionInfoMessage,
    blockingMessage: multiplexSelectionBlockingMessage,
    isLoadingSelection,
    selectedMultiplexSlideIds,
    selectedMultiplexSlides,
  } = useMultiplexActionSelection(casesParams);
  const multiplexCasesParams = effectiveCasesParams || casesParams;
  const studyId = multiplexCasesParams.studyId ?? multiplexCasesParams.filters?.studyId;
  const resolvedStudyId = typeof studyId === 'string' ? studyId : undefined;
  const orchestrationSelectionState = useOrchestrationSelections();
  const { selectedOrchestrations } = orchestrationSelectionState;
  const binaryClassifierCasesParams = useMemo(
    () => (resolvedStudyId ? { ...multiplexCasesParams, studyId: resolvedStudyId } : multiplexCasesParams),
    [multiplexCasesParams, resolvedStudyId]
  );
  const selectedModelPath = formMethods.watch('modelPath');
  const selectedChannelNames = formMethods.watch('channelNames') || [];
  const selectedInputArtifactUrlPatterns: MapArtifactToSlides[] = useMemo(
    () => getMapArtifactToSlides(selectedMultiplexSlideIds, selectedOrchestrations),
    [selectedMultiplexSlideIds, selectedOrchestrations]
  );
  const selectedCellSegmentationSlideCount = useMemo(
    () => size(flatMap(selectedInputArtifactUrlPatterns, 'slideIds')),
    [selectedInputArtifactUrlPatterns]
  );
  const hasFormValueChanges =
    (selectedModelPath || '') !== baselineModelPath ||
    Boolean(formMethods.formState.dirtyFields.channelNames) ||
    selectedCellSegmentationSlideCount > 0;
  const selectedModelId = getModelId(selectedModelPath || '');
  const needsSelectedSlideMetadata =
    size(selectedMultiplexSlideIds) > 0 && size(selectedMultiplexSlides) !== size(selectedMultiplexSlideIds);

  const { data: selectedModel } = useQuery({
    queryKey: ['model', selectedModelId],
    queryFn: ({ signal }) => getModel(selectedModelId, signal),
    retry: false,
    enabled: Boolean(selectedModelId),
  });
  const selectedModelDescription = selectedModel?.meta?.description;

  useTrackActionFormDirty(hasFormValueChanges);

  const {
    data: normalizationSummaries,
    isLoading: isLoadingNormalizationSummaries,
    isError: isNormalizationSummariesError,
    isFetching: isFetchingNormalizationSummaries,
    refetch: refetchNormalizationSummaries,
  } = useQuery({
    queryKey: ['multiplexBinaryClassifierNormalizationSummary', binaryClassifierCasesParams],
    queryFn: ({ signal }) => getNormalizationSummary(binaryClassifierCasesParams, signal),
    enabled: Boolean(effectiveCasesParams) && Boolean(resolvedStudyId),
  });
  const { data: channelMarkersResponse, isLoading: isLoadingChannelMarkers } = useQuery({
    queryKey: getChannelMarkersQueryKey(binaryClassifierCasesParams),
    queryFn: ({ signal }) => getChannelMarkers(binaryClassifierCasesParams, signal),
    enabled: Boolean(effectiveCasesParams) && Boolean(resolvedStudyId),
  });
  const channelMarkersInAllSlides = useMemo(
    () => getChannelMarkersInAllSlides(channelMarkersResponse),
    [channelMarkersResponse]
  );
  const markerNormalizationAvailability = useMemo(
    () =>
      getMarkerNormalizationAvailability({
        normalizationSummaries,
        slideIds: selectedMultiplexSlideIds,
        markerIds: channelMarkersInAllSlides,
      }),
    [channelMarkersInAllSlides, normalizationSummaries, selectedMultiplexSlideIds]
  );
  const normalizedChannelMarkers = useMemo(
    () =>
      isLoadingNormalizationSummaries || isNormalizationSummariesError
        ? []
        : markerNormalizationAvailability.normalizedMarkerIds,
    [
      isLoadingNormalizationSummaries,
      isNormalizationSummariesError,
      markerNormalizationAvailability.normalizedMarkerIds,
    ]
  );
  const normalizationSummaryErrorAlert = (
    <Alert
      severity="error"
      data-testid="binary-classifier-normalization-summary-error"
      action={
        <Button
          color="inherit"
          size="small"
          disabled={isFetchingNormalizationSummaries}
          onClick={() => {
            void refetchNormalizationSummaries();
          }}
        >
          Retry
        </Button>
      }
    >
      {
        "Couldn't load marker normalization status, so markers can't be checked yet. Retry before running the binary classifier."
      }
    </Alert>
  );
  const channelSelectSchema = useMemo(
    () =>
      buildCohortWideChannelSchema(channelMarkersInAllSlides, {
        inputLabel: 'Marker',
      })
        .test('is-normalized-marker', 'Marker is not normalized', function (value) {
          const notNormalizedMarker = find(value, (markerId) => !includes(normalizedChannelMarkers, markerId));

          return notNormalizedMarker
            ? this.createError({
                message: `Marker: ${stainTypeIdToDisplayName(
                  notNormalizedMarker
                )} is not normalized on every selected slide`,
              })
            : true;
        })
        .min(1, 'At least one marker is required'),
    [channelMarkersInAllSlides, normalizedChannelMarkers, stainTypeIdToDisplayName]
  );
  const {
    data: cellSegmentationArtifacts,
    isFetching: isFetchingCellSegmentationArtifacts,
    isError: isCellSegmentationArtifactsError,
  } = useArtifactResultsQuery<ModelInference>({
    caseParams: binaryClassifierCasesParams,
    config: CellSegmentationConfig,
    enabled: Boolean(effectiveCasesParams) && Boolean(resolvedStudyId),
  });
  const { data: selectedSlidesMetadata, isFetching: isFetchingSelectedSlidesMetadata } = useQuery({
    queryKey: ['multiplexBinaryClassifierSlides', binaryClassifierCasesParams],
    queryFn: ({ signal }) => getSlidesDataForArtifactsResults(binaryClassifierCasesParams, signal),
    enabled: Boolean(effectiveCasesParams) && Boolean(resolvedStudyId) && needsSelectedSlideMetadata,
  });
  const executionRequirements = useExecutionRequirements([
    {
      type: ExecutionRequirementType.CloudOptimizedSlides,
      label: 'Cloud-optimized slides',
      casesParams: binaryClassifierCasesParams,
      slideIds: selectedMultiplexSlideIds,
      enabled: Boolean(effectiveCasesParams) && Boolean(resolvedStudyId),
    },
  ]);

  const runMultiplexBinaryClassifierMutation = useMutation(runMultiplexBinaryClassifier, {
    onError: () => {
      enqueueSnackbar('Error occurred, multiplex binary classifier failed', {
        variant: 'error',
      });
    },
    onSuccess: () => {
      enqueueSnackbar('Multiplex binary classifier started', { variant: 'success' });
    },
    onSettled() {
      closeSnackbar(SNACK_BAR_KEY_RUN_MULTIPLEX_BINARY_CLASSIFIER);
    },
  });

  const missingSelectedCellSegmentationSlideIds = useMemo(
    () =>
      getMissingSelectedArtifactResultSlideIds({
        selectedSlideIds: selectedMultiplexSlideIds,
        inputArtifactUrlPatterns: selectedInputArtifactUrlPatterns,
      }),
    [selectedInputArtifactUrlPatterns, selectedMultiplexSlideIds]
  );
  const normalizationCoverageSummary = useNormalizationCoverageSummary({
    normalizationSummaries,
    selectedSlideIds: selectedMultiplexSlideIds,
    selectedMarkerIds: selectedChannelNames,
  });
  const isModelSelectionValid = modelUrlValidation.isValidSync(formMethods.getValues());
  const isSelectedInputSelectionValid =
    channelSelectSchema.isValidSync(selectedChannelNames) &&
    selectedCellSegmentationSlideCount === size(selectedMultiplexSlideIds) &&
    isEmpty(missingSelectedCellSegmentationSlideIds);
  const isNormalizationCoverageComplete = normalizationCoverageSummary.isComplete;
  const isCheckingPrerequisites =
    executionRequirements.isLoading ||
    (Boolean(selectedModelPath) &&
      (isLoadingNormalizationSummaries ||
        isLoadingChannelMarkers ||
        isFetchingCellSegmentationArtifacts ||
        isFetchingSelectedSlidesMetadata));
  const canRunBinaryClassifier =
    isModelSelectionValid &&
    isSelectedInputSelectionValid &&
    executionRequirements.canExecute &&
    !isCheckingPrerequisites &&
    !isCellSegmentationArtifactsError &&
    !isNormalizationSummariesError &&
    isNormalizationCoverageComplete;

  const onSubmit = () => {
    runMultiplexBinaryClassifierMutation.mutate({
      ...binaryClassifierCasesParams,
      ...formMethods.getValues(),
      inputArtifactUrlPatterns: selectedInputArtifactUrlPatterns,
    });

    enqueueSnackbar({
      variant: 'success',
      message: (
        <Grid container>
          <Grid item>
            <Typography>Waiting for multiplex binary classifier to start</Typography>
          </Grid>
          <Grid item>
            <CircularProgress sx={{ marginLeft: 10 }} color="inherit" size={20} />
          </Grid>
        </Grid>
      ),
      key: SNACK_BAR_KEY_RUN_MULTIPLEX_BINARY_CLASSIFIER,
      autoHideDuration: null,
    });

    onClose(undefined, true);
  };

  const selectedSlidesMetadataList = Array.isArray(selectedSlidesMetadata) ? selectedSlidesMetadata : [];
  const selectedSlideDetailsById = useMemo(
    () =>
      reduce(
        [...selectedMultiplexSlides, ...selectedSlidesMetadataList],
        (accumulator, slide) => {
          if (includes(selectedMultiplexSlideIds, slide.slideId)) {
            accumulator[slide.slideId] = slide;
          }

          return accumulator;
        },
        {} as Record<string, (typeof selectedMultiplexSlides)[number]>
      ),
    [selectedMultiplexSlideIds, selectedMultiplexSlides, selectedSlidesMetadataList]
  );
  const selectedSlidesForArtifactSelection = useMemo(
    () =>
      map(
        selectedMultiplexSlideIds,
        (slideId) =>
          selectedSlideDetailsById[slideId] ||
          ({
            slideId,
            stainingType: MULTIPLEX_STAIN_ID,
            originalFileName: slideId,
            caseId: '',
            caseLabel: '',
            negativeControl: false,
          } as SlideInferenceResults)
      ),
    [selectedMultiplexSlideIds, selectedSlideDetailsById]
  );
  const getSlideLabel = React.useCallback(
    (slideId: string) => {
      const slideDetails = selectedSlideDetailsById[slideId];
      const slideName = slideDetails?.caseLabel || slideDetails?.originalFileName;

      return slideName && slideName !== slideId ? `${slideName} (${slideId})` : slideId;
    },
    [selectedSlideDetailsById]
  );
  const renderNormalizationSlideCell = React.useCallback(
    ({ slideId }: { slideId: string; markerIdsToShow: string[] }) => {
      const slideDetails = selectedSlideDetailsById[slideId];
      const slideDisplayLabel = slideDetails?.caseLabel ? `${slideDetails.caseLabel} - ${slideId}` : slideId;
      const slide = {
        id: slideId,
        labId: binaryClassifierCasesParams.labId,
        stainingType: slideDetails?.stainingType,
        originalFileName: slideDetails?.originalFileName,
      } as Slide;

      return (
        <SlidePreviewCell
          slides={[slide]}
          caseStudyId={resolvedStudyId}
          labId={binaryClassifierCasesParams.labId}
          summaryLabel={slideDisplayLabel}
        />
      );
    },
    [binaryClassifierCasesParams.labId, resolvedStudyId, selectedSlideDetailsById]
  );
  const hasMissingPrerequisites =
    executionRequirements.hasBlockingFailures ||
    isCellSegmentationArtifactsError ||
    isNormalizationSummariesError ||
    !isEmpty(missingSelectedCellSegmentationSlideIds) ||
    !isNormalizationCoverageComplete;
  const shouldShowModelDescriptionInHeader =
    activeStepIndex !== modelSelectionStepIndex && Boolean(selectedModelDescription);

  const steps = {
    [RunMultiplexBinaryClassifierSteps.CheckProcesses]: {
      label: RunMultiplexBinaryClassifierSteps.CheckProcesses,
      isLoading: isCheckingPrerequisites,
      content: (
        <Grid item>
          {!isModelSelectionValid ? (
            <Typography>Select a binary classifier model before checking required inputs.</Typography>
          ) : isCheckingPrerequisites ? (
            <Grid container alignItems="center" spacing={1}>
              <Grid item>
                <CircularProgress size={20} />
              </Grid>
              <Grid item>
                <Typography>
                  Checking selected cell segmentation artifacts and marker normalization coverage.
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <Grid container direction="column" spacing={1}>
              <Grid item>
                <ExecutionRequirementsPanel requirements={executionRequirements.requirements} />
              </Grid>
              {hasMissingPrerequisites ? (
                <>
                  <Grid item>
                    Some selected slides are missing required binary classifier inputs. The binary classifier needs cell
                    segmentation results and normalization values for each selected marker.
                  </Grid>
                  {isCellSegmentationArtifactsError ? (
                    <Grid item>
                      <Typography color="error">Error fetching cell segmentation artifacts.</Typography>
                    </Grid>
                  ) : null}
                  {!isEmpty(missingSelectedCellSegmentationSlideIds) ? (
                    <Grid item xs={12}>
                      <MultiplexProcessSlideIds
                        flowClassName="Selected cell segmentation results"
                        slideIds={missingSelectedCellSegmentationSlideIds}
                        slideDetailsById={selectedSlideDetailsById}
                      />
                    </Grid>
                  ) : null}
                  {isNormalizationSummariesError ? (
                    <Grid item xs={12}>
                      {normalizationSummaryErrorAlert}
                    </Grid>
                  ) : !isNormalizationCoverageComplete ? (
                    <Grid item xs={12}>
                      <NormalizationCoverageSummaryPanel
                        coverageSummary={normalizationCoverageSummary}
                        getMarkerLabel={(markerId) => markerId}
                        renderSlideCell={renderNormalizationSlideCell}
                        noCoverageMessage="No channel normalization records were found for the selected slides."
                      />
                    </Grid>
                  ) : null}
                </>
              ) : (
                <Grid item>
                  <Typography>
                    All selected slides have the selected cell segmentation results and selected marker normalization
                    values. Execute the binary classifier when ready.
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </Grid>
      ),
    },
    [RunMultiplexBinaryClassifierSteps.SelectInputs]: {
      label: RunMultiplexBinaryClassifierSteps.SelectInputs,
      subLabel:
        activeStepIndex !== inputSelectionStepIndex
          ? `${selectedCellSegmentationSlideCount}/${size(selectedMultiplexSlideIds)} slides, ${size(
              selectedChannelNames
            )} markers`
          : undefined,
      isLoading:
        isFetchingCellSegmentationArtifacts ||
        isFetchingSelectedSlidesMetadata ||
        isLoadingChannelMarkers ||
        isLoadingNormalizationSummaries,
      content: (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <ChannelsController
              name="channelNames"
              error={Boolean(formMethods.formState.errors.channelNames)}
              helperText={formMethods.formState.errors.channelNames?.message}
              methods={formMethods}
              label="Markers to classify"
              placeholder="Select markers to classify"
              allowedChannelNames={normalizedChannelMarkers}
              defaultSelectedChannelsOnLoad={normalizedChannelMarkers}
              additionalOnChange={() => {
                void formMethods.trigger('channelNames');
              }}
              casesParams={binaryClassifierCasesParams}
            />
          </Grid>
          {isNormalizationSummariesError ? (
            <Grid item>{normalizationSummaryErrorAlert}</Grid>
          ) : !isLoadingNormalizationSummaries && !isLoadingChannelMarkers ? (
            <Grid item>
              <MarkerNormalizationGapAlert
                gapGroups={markerNormalizationAvailability.gapGroups}
                getMarkerLabel={stainTypeIdToDisplayName}
                getSlideLabel={getSlideLabel}
              />
            </Grid>
          ) : null}
          <Grid item>
            {isCellSegmentationArtifactsError ? (
              <Typography color="error">Error fetching cell segmentation artifacts.</Typography>
            ) : null}
            {selectedCellSegmentationSlideCount === 0 ? (
              <Typography color="error">Select a cell segmentation result for each selected slide.</Typography>
            ) : null}
            <InferenceModelsForSlides
              studyId={resolvedStudyId || ''}
              slides={selectedSlidesForArtifactSelection}
              modelsType={getModelsTypeByModelInferences(cellSegmentationArtifacts || [])}
              inferenceModels={cellSegmentationArtifacts || []}
              isLoading={isFetchingCellSegmentationArtifacts || isFetchingSelectedSlidesMetadata}
              {...orchestrationSelectionState}
            />
          </Grid>
          {!isEmpty(missingSelectedCellSegmentationSlideIds) ? (
            <Grid item xs={12}>
              <MultiplexProcessSlideIds
                flowClassName="Selected cell segmentation results"
                slideIds={missingSelectedCellSegmentationSlideIds}
                slideDetailsById={selectedSlideDetailsById}
              />
            </Grid>
          ) : null}
        </Grid>
      ),
      onNextOrBackClick: () => {
        void formMethods.trigger('channelNames');
      },
    },
    [RunMultiplexBinaryClassifierSteps.SelectModel]: {
      label: 'Model Selection',
      subLabel: selectedModelPath || 'Please select a model',
      subDescription: shouldShowModelDescriptionInHeader ? selectedModelDescription : undefined,
      content: (
        <SelectModelStep<IFormValues, 'modelPath'>
          modelUrlField="modelPath"
          casesParams={binaryClassifierCasesParams}
          formMethods={formMethods}
          modelType={modelTypeBinaryClassifier.apiModelValue}
          slideStain={MULTIPLEX_STAIN_ID}
          onDefaultModelUrlApplied={(defaultModelUrl) => {
            setBaselineModelPath(defaultModelUrl);

            if (defaultModelUrl && !didAdvanceFromDefaultModel && activeStepIndex === modelSelectionStepIndex) {
              setDidAdvanceFromDefaultModel(true);
              setActiveStepIndex(inputSelectionStepIndex);
            }
          }}
        />
      ),
    },
  };

  const [didChangeSteps, setDidChangeSteps] = useState(false);
  const isStepFailed: Record<number, boolean> = {
    [modelSelectionStepIndex]: didChangeSteps && !isModelSelectionValid,
    [inputSelectionStepIndex]: didChangeSteps && !isSelectedInputSelectionValid,
    [checkProcessesStepIndex]: isCheckingPrerequisites || !canRunBinaryClassifier,
  };

  if (isLoadingSelection) {
    return (
      <MultiplexSelectionAlert
        severity="info"
        title="Checking slide selection"
        message="Resolving the multiplex slides for the current case selection."
      />
    );
  }

  if (multiplexSelectionBlockingMessage) {
    return (
      <MultiplexSelectionAlert
        severity="error"
        title="Select slides for this multiplex action"
        message={multiplexSelectionBlockingMessage}
      />
    );
  }

  return (
    <Grid container direction="column" spacing={2}>
      {multiplexSelectionInfoMessage ? (
        <Grid item>
          <MultiplexSelectionAlert
            severity="info"
            title="This action runs on slides"
            message={multiplexSelectionInfoMessage}
          />
        </Grid>
      ) : null}
      <Grid item>
        <ActionStepper
          validateStep={async () => !isStepFailed[activeStepIndex]}
          hideExecution={!canRunBinaryClassifier}
          handleSubmit={onSubmit}
          steps={map(stepsOrder, (step) => steps[step])}
          setActiveStepForValidation={(newStep) => {
            setActiveStepIndex(newStep);
            setDidChangeSteps(true);
          }}
          isStepFailed={isStepFailed}
          activeStateHandlers={[activeStepIndex, setActiveStepIndex]}
        />
      </Grid>
    </Grid>
  );
};
